import React, { useState, useEffect, useMemo } from 'react';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { X, Wrench, Plus, Trash2, ShoppingCart, Search, AlertTriangle } from 'lucide-react';
import api, { maintenanceCatalogAPI, supplierAPI } from '../../api.js';
import { DURATION_PRESETS, DEFAULT_DURATION, formatDuration, computePickupTime } from '../../utils/maintenanceDuration.js';
import { useFormat } from '../../i18n/format.js';

/**
 * MaintenanceFormModal V2 — Catalog picker + operations cart
 *
 * Two-column layout:
 * - Left: Vehicle selector, origin, category tabs, operation grid
 * - Right: Operations cart with running total, priority, garage, notes
 */
function MaintenanceFormModal({ isOpen, onClose, onSuccess, onMessage, vehicles, drivers, defaultVehicleId }) {
  // Saisie dans la devise de la filiale active - un seul outil monetaire.
  const { moneyScope, scopeSymbol } = useOrgCurrency();
  const { t, fmt } = useFormat();
  // Form state
  const [vehicleId, setVehicleId] = useState('');
  const [origin, setOrigin] = useState('scheduled');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [garageId, setGarageId] = useState('');
  // Optional direct-plan fields: when filled, the "Créer et planifier" button
  // is active and skips the "Créée" state — lands the request in "Planifiée"
  // in one gesture (scenario 2: manager already has the RDV at intake).
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [estimatedDuration, setEstimatedDuration] = useState(DEFAULT_DURATION);
  const [loading, setLoading] = useState(false);

  // Catalog state
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Cart state (operations to include)
  const [cart, setCart] = useState([]);

  // Garage suppliers
  const [garages, setGarages] = useState([]);

  // Existing planned MRs for the selected vehicle — used to warn about
  // duplicate appointments. Populated when vehicleId changes.
  const [plannedDuplicates, setPlannedDuplicates] = useState([]);

  // Load catalog + garages on open
  useEffect(() => {
    if (!isOpen) return;
    setCatalogLoading(true);
    maintenanceCatalogAPI.getAll()
      .then(res => {
        const items = res?.data || res || [];
        setCatalog(items);
        // Auto-select first category
        if (items.length > 0) {
          const cats = [...new Set(items.map(i => i.category_code))];
          setSelectedCategory(cats[0]);
        }
      })
      .catch(err => console.error('Catalog load error:', err))
      .finally(() => setCatalogLoading(false));

    supplierAPI.getAll()
      .then(res => {
        const all = res?.data || res || [];
        setGarages(all.filter(s => s.category === 'garage'));
      })
      .catch(() => {});
  }, [isOpen]);

  // Pre-fill vehicle
  useEffect(() => {
    if (defaultVehicleId && isOpen) {
      setVehicleId(String(defaultVehicleId));
    }
  }, [defaultVehicleId, isOpen]);

  // Detect duplicate planned MRs for the selected vehicle
  useEffect(() => {
    if (!isOpen || !vehicleId) {
      setPlannedDuplicates([]);
      return;
    }
    let cancelled = false;
    const todayYMD = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    api.maintenance.getAll()
      .then(res => {
        if (cancelled) return;
        const list = res?.data || [];
        // Only future-dated appointments (or undated) qualify as live duplicates;
        // a past planned MR is a stale state, not a real conflict.
        const dupes = list.filter(m =>
          m.status === 'planned' &&
          String(m.vehicle_id) === String(vehicleId) &&
          (!m.scheduled_date || m.scheduled_date >= todayYMD)
        );
        setPlannedDuplicates(dupes);
      })
      .catch(() => { if (!cancelled) setPlannedDuplicates([]); });
    return () => { cancelled = true; };
  }, [isOpen, vehicleId]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setVehicleId('');
      setOrigin('scheduled');
      setPriority('medium');
      setNotes('');
      setGarageId('');
      setScheduledDate('');
      setScheduledTime('09:00');
      setEstimatedDuration(DEFAULT_DURATION);
      setCart([]);
      setSearchTerm('');
      setSelectedCategory(null);
    }
  }, [isOpen]);

  // Group catalog by category
  const categories = useMemo(() => {
    const map = new Map();
    catalog.forEach(item => {
      if (!map.has(item.category_code)) {
        map.set(item.category_code, {
          code: item.category_code,
          label: item.category_label,
          icon: item.category_icon,
          items: []
        });
      }
      map.get(item.category_code).items.push(item);
    });
    return [...map.values()];
  }, [catalog]);

  // Filtered operations (by category + search)
  const filteredOps = useMemo(() => {
    let ops = catalog;
    if (selectedCategory) {
      ops = ops.filter(o => o.category_code === selectedCategory);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      ops = ops.filter(o =>
        o.title.toLowerCase().includes(term) ||
        o.description?.toLowerCase().includes(term) ||
        o.category_label.toLowerCase().includes(term)
      );
    }
    return ops;
  }, [catalog, selectedCategory, searchTerm]);

  // Cart totals
  const cartTotal = cart.reduce((sum, item) => sum + (parseFloat(item.quote_amount) || 0), 0);
  const cartTitle = cart.length > 0
    ? cart.map(c => c.title).join(' + ')
    : '';

  // Add operation to cart
  const addToCart = (catalogItem) => {
    // Prevent duplicate
    if (cart.find(c => c.operation_code === catalogItem.operation_code)) return;
    setCart(prev => [...prev, {
      category_code: catalogItem.category_code,
      operation_code: catalogItem.operation_code,
      title: catalogItem.title,
      description: catalogItem.description,
      quote_amount: catalogItem.reference_price || 0,
      resource_types_reset: catalogItem.resource_types_reset || '',
      category_label: catalogItem.category_label,
    }]);
  };

  // Remove from cart
  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  // Update quote amount in cart
  const updateCartAmount = (index, amount) => {
    setCart(prev => prev.map((item, i) => i === index ? { ...item, quote_amount: amount } : item));
  };

  // Submit
  // mode: 'draft' (default) → create in 'created' state, manager will plan later.
  // mode: 'planned' → create + auto-schedule if garage + date already known
  //                    (scenario 2: manager has the RDV at intake).
  const handleSubmit = async (mode = 'draft') => {
    if (!vehicleId) {
      onMessage?.({ title: t('maintenance.messages.errorTitle'), message: t('maintenance.messages.vehicleRequired'), type: 'error' });
      return;
    }
    if (cart.length === 0) {
      onMessage?.({ title: t('maintenance.messages.errorTitle'), message: t('maintenance.messages.operationRequired'), type: 'error' });
      return;
    }
    if (mode === 'planned' && (!garageId || !scheduledDate)) {
      onMessage?.({ title: t('maintenance.messages.errorTitle'), message: t('maintenance.messages.planFieldsRequired'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      // 1. Create maintenance request (always starts in 'created')
      const requestData = {
        vehicle_id: parseInt(vehicleId),
        title: cartTitle.substring(0, 200),
        description: cart.map(c => c.title).join(', '),
        type: cart[0]?.category_code === 'freins' ? 'brake_service'
            : cart[0]?.category_code === 'pneus' ? 'tire_rotation'
            : cart[0]?.category_code === 'revision' ? 'oil_change'
            : 'other',
        priority,
        status: 'created',
        origin,
        notes: notes || null,
        quote_total: cartTotal,
        garage_supplier_id: garageId ? parseInt(garageId) : null,
      };

      const createRes = await api.maintenance.create(requestData);
      const requestId = createRes?.data?.id;

      if (!requestId) throw new Error('Failed to create maintenance request');

      // 2. Add operations to the request
      for (const op of cart) {
        await api.maintenance.addOperation(requestId, {
          category_code: op.category_code,
          operation_code: op.operation_code,
          title: op.title,
          description: op.description,
          quote_amount: parseFloat(op.quote_amount) || 0,
          resource_types_reset: op.resource_types_reset,
        });
      }

      // 3. If "Créer et planifier": immediately transition to 'planned'
      if (mode === 'planned') {
        await api.maintenance.schedule(requestId, {
          garage_supplier_id: parseInt(garageId),
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime || null,
          estimated_duration_days: Number(estimatedDuration) || DEFAULT_DURATION,
        });
      }

      onMessage?.({
        title: mode === 'planned' ? t('maintenance.messages.createdAndPlannedTitle') : t('maintenance.messages.createdTitle'),
        message: t('maintenance.messages.createdDetail', { id: requestId, count: cart.length, amount: moneyScope(cartTotal) }),
        type: 'success'
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error creating maintenance request:', error);
      onMessage?.({
        title: t('maintenance.messages.errorTitle'),
        message: t('maintenance.messages.createError', { error: error.message || t('maintenance.messages.unknownError') }),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const CATEGORY_ICONS = {
    mecanique: '🔧', freins: '🛑', pneus: '⭕', carrosserie: '🚗',
    electricite: '⚡', climatisation: '❄️', vitrage: '🪟', revision: '📋',
    controle: '✅', autre: '📦'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-amber-500 text-white px-6 py-4 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <Wrench className="w-6 h-6" />
            <h2 className="text-lg font-bold">{t('maintenance.form.title')}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — 2 columns */}
        <div className="flex-1 overflow-hidden flex">
          {/* LEFT: Vehicle + Catalog */}
          <div className="w-3/5 border-r overflow-y-auto p-5 space-y-4">
            {/* Duplicate planned MR warning */}
            {plannedDuplicates.length > 0 && (
              <div className="border border-orange-300 bg-orange-50 rounded-lg p-3 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-orange-800">
                    {t('maintenance.form.duplicateWarning', { count: plannedDuplicates.length })}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-orange-700">
                    {plannedDuplicates.slice(0, 3).map(m => (
                      <li key={m.id}>
                        #{m.id} — {(m.title || '').slice(0, 60)}
                        {m.scheduled_date && ` ${t('maintenance.form.duplicateAppointment', { date: fmt.date(m.scheduled_date) })}`}
                        {m.garage_name && ` ${t('maintenance.form.duplicateGarage', { garage: m.garage_name })}`}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1.5 text-xs text-orange-600">
                    {t('maintenance.form.duplicateHint')}
                  </div>
                </div>
              </div>
            )}
            {/* Vehicle + Origin */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.vehicle')}</label>
                <select
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">{t('common.actions.selectPlaceholder')}</option>
                  {(vehicles || []).filter(v => v.status !== 'retired' && v.status !== 'decommissioned').map(v => (
                    <option key={v.id} value={v.id}>
                      {v.license_plate} — {v.make} {v.model}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.origin')}</label>
                <div className="flex gap-2">
                  {[
                    { value: 'scheduled', label: t('maintenance.origin.scheduled') },
                    { value: 'problem_found', label: t('maintenance.origin.problem_found') },
                  ].map(o => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setOrigin(o.value)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                        origin === o.value
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); if (e.target.value) setSelectedCategory(null); }}
                placeholder={t('maintenance.form.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              />
            </div>

            {/* Category tabs */}
            <div className="flex flex-wrap gap-1.5">
              {categories.map(cat => (
                <button
                  key={cat.code}
                  type="button"
                  onClick={() => { setSelectedCategory(cat.code); setSearchTerm(''); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedCategory === cat.code
                      ? 'bg-amber-100 text-amber-800 border border-amber-300'
                      : 'bg-slate-100 text-slate-600 border border-transparent hover:bg-slate-200'
                  }`}
                >
                  {CATEGORY_ICONS[cat.code] || '📋'} {cat.label} ({cat.items.length})
                </button>
              ))}
            </div>

            {/* Operation grid */}
            {catalogLoading ? (
              <div className="text-center py-8 text-slate-400 text-sm">{t('maintenance.form.catalogLoading')}</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {filteredOps.map(op => {
                  const inCart = cart.find(c => c.operation_code === op.operation_code);
                  return (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => !inCart && addToCart(op)}
                      disabled={!!inCart}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        inCart
                          ? 'border-green-300 bg-green-50 opacity-60 cursor-default'
                          : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-800">{op.title}</span>
                        {!inCart && <Plus className="w-4 h-4 text-amber-500 shrink-0" />}
                        {inCart && <span className="text-xs text-green-600">{t('maintenance.form.added')}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 line-clamp-1">{op.description}</div>
                      {op.reference_price > 0 && (
                        <div className="text-xs text-amber-600 mt-1 font-medium">
                          {t('maintenance.form.referencePrice', { amount: moneyScope(op.reference_price) })}
                          {op.typical_duration_hours ? ` • ${t('maintenance.form.approxDuration', { hours: op.typical_duration_hours })}` : ''}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT: Cart + Settings */}
          <div className="w-2/5 overflow-y-auto p-5 bg-slate-50 flex flex-col">
            {/* Cart header */}
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">
                {t('maintenance.form.cartTitle', { count: cart.length })}
              </span>
            </div>

            {/* Cart items */}
            {cart.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                {t('maintenance.form.cartEmpty')}
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {cart.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                            {CATEGORY_ICONS[item.category_code] || '📋'}
                          </span>
                          <span className="text-sm font-medium text-slate-800 truncate">{item.title}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFromCart(idx)}
                        className="text-slate-400 hover:text-red-500 shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-slate-500">{t('maintenance.form.quoteLabel')}</label>
                      <input
                        type="number"
                        value={item.quote_amount}
                        onChange={(e) => updateCartAmount(idx, e.target.value)}
                        className="w-24 px-2 py-1 border border-slate-300 rounded text-sm text-right"
                        min="0"
                        step="0.01"
                      />
                      <span className="text-xs text-slate-500">{scopeSymbol}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Running total */}
            {cart.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-amber-800">{t('maintenance.form.quoteTotal')}</span>
                  <span className="text-lg font-bold text-amber-700">
                    {moneyScope(cartTotal)}
                  </span>
                </div>
              </div>
            )}

            {/* Settings */}
            <div className="space-y-3 mt-auto">
              {/* Priority */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.priority')}</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { value: 'low', label: t('maintenance.priority.low'), cls: 'text-slate-600 border-slate-200 bg-slate-50' },
                    { value: 'medium', label: t('maintenance.priority.medium'), cls: 'text-blue-600 border-blue-200 bg-blue-50' },
                    { value: 'high', label: t('maintenance.priority.high'), cls: 'text-amber-600 border-amber-200 bg-amber-50' },
                    { value: 'critical', label: t('maintenance.priority.critical'), cls: 'text-red-600 border-red-200 bg-red-50' },
                  ].map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setPriority(p.value)}
                      className={`px-2 py-1.5 rounded-md border text-xs font-medium transition-all ${
                        priority === p.value
                          ? p.cls + ' ring-1 ring-offset-1'
                          : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Garage */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.garage')}</label>
                <select
                  value={garageId}
                  onChange={(e) => setGarageId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="">{t('maintenance.form.garageLater')}</option>
                  {garages.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name || g.company_name} — {g.city || ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scenario 2: direct plan at intake (RDV already booked) */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.scheduledDate')}</label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.scheduledTime')}</label>
                  <input
                    type="time"
                    step="900"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.estimatedDuration')}</label>
                  <select
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                  >
                    {DURATION_PRESETS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {garageId && scheduledDate && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                  {t('maintenance.form.planHint')}
                  {' '}
                  {estimatedDuration < 1
                    ? (scheduledTime
                        ? t('maintenance.form.planShortAt', { duration: formatDuration(estimatedDuration), time: computePickupTime(scheduledTime, estimatedDuration) })
                        : t('maintenance.form.planShort', { duration: formatDuration(estimatedDuration) }))
                    : estimatedDuration === 1
                      ? (scheduledTime
                          ? t('maintenance.form.planFullDayAt', { time: computePickupTime(scheduledTime, 1) })
                          : t('maintenance.form.planFullDay'))
                      : t('maintenance.form.planMultiDay', { duration: formatDuration(estimatedDuration) })}
                </p>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('maintenance.form.notes')}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  rows={2}
                  placeholder={t('maintenance.form.notesPlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-3 flex justify-between items-center bg-white shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
            disabled={loading}
          >
            {t('common.actions.cancel')}
          </button>
          <div className="flex items-center gap-3">
            {cart.length > 0 && (
              <span className="text-sm text-slate-500">
                {t('maintenance.form.cartSummary', { count: cart.length, amount: moneyScope(cartTotal) })}
              </span>
            )}
            <button
              type="button"
              onClick={() => handleSubmit('draft')}
              disabled={loading || cart.length === 0 || !vehicleId}
              className="px-4 py-2 bg-white border border-amber-500 text-amber-700 rounded-lg hover:bg-amber-50 flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
              title={t('maintenance.form.submitHint')}
            >
              <Wrench className="w-4 h-4" />
              {loading ? '...' : t('maintenance.form.submit')}
            </button>
            <button
              type="button"
              onClick={() => handleSubmit('planned')}
              disabled={loading || cart.length === 0 || !vehicleId || !garageId || !scheduledDate}
              className="px-5 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
              title={t('maintenance.form.submitAndPlanHint')}
            >
              <Wrench className="w-4 h-4" />
              {loading ? '...' : t('maintenance.form.submitAndPlan')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceFormModal;
